import { memo, useCallback } from "react";
import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { fetchProfile, useUser } from "@/entities/Auth";
import { AccountActionText } from "@/shared/config/strings";
import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { useAppDispatch } from "@/shared/lib/hooks/redux";
import { useConfirmModal } from "@/shared/lib/hooks/useConfirmModal";
import { ActionBtn } from "@/shared/ui/Btn";

import { SubscriptionStatus } from "../../const/subscription";
import { getDetails } from "../../model/selectors/subscriptionSelectors";
import { cancelNextProduct } from "../../model/services/cancelNextProduct";

import cls from "./CurrentPlan.module.scss";

interface CurrentPlanProps {
  className?: string;
}

export const CurrentPlan = memo(({ className }: CurrentPlanProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useUser();
  const { showAlert } = useAlert();

  const blockClassName = clsx(cls.CurrentPlan, className);

  const subscriptionDetails = useSelector(getDetails);

  const { modalNode, withModalConfirm } = useConfirmModal();

  const onClickCancel = useCallback(async () => {
    const result = await dispatch(cancelNextProduct());

    if (result.meta.requestStatus === "fulfilled") {
      showAlert({
        theme: AlertTheme.SUCCESS,
        message: "Successfully canceled!",
      } as AlertOptions);

      dispatch(fetchProfile());
    } else {
      const error = result.payload as { details?: string } | undefined;
      return showAlert({
        theme: AlertTheme.ERROR,
        message: error?.details || "Failed to cancel change",
      } as AlertOptions);
    }
  }, [dispatch, showAlert]);

  if (!subscriptionDetails) {
    return null;
  }

  const { currentPeriodStart, currentPeriodEnd, interval, paymentPausedAt, paymentResumesAt } = subscriptionDetails;
  const start = new Date(currentPeriodStart * 1000).toDateString();
  const end = new Date(currentPeriodEnd * 1000).toDateString();
  const pauseAt = new Date(paymentPausedAt).toDateString();
  const resumes = new Date(paymentResumesAt).toDateString();

  let content;

  if (subscriptionDetails.currentSubscriptionStatus === SubscriptionStatus.Canceled) {
    content = (
      <div className={blockClassName}>
        You are not currently a Sibyl member. Reactivate your Sibyl membership to get access to unlimited Tarot
        readings, at only $19/month.
      </div>
    );
  } else if (subscriptionDetails.currentSubscriptionStatus === SubscriptionStatus.CancelUpcoming) {
    content = (
      <div className={blockClassName}>
        You are currently on the {interval}
        ly Sibyl membership plan from {start} - {end}, after which it will be canceled. Reactivate to get uninterrupted
        access to unlimited Tarot readings.
      </div>
    );
  } else if (subscriptionDetails.currentSubscriptionStatus === SubscriptionStatus.MonthlySkipping) {
    content = (
      <div className={blockClassName}>
        Your Sibyl membership is currently paused until {resumes}. Reactivate your plan to get immediate access to
        unlimited Tarot readings.
      </div>
    );
  } else if (subscriptionDetails.currentSubscriptionStatus === SubscriptionStatus.MonthlySkipUpcoming) {
    content = (
      <div className={blockClassName}>
        You are currently on the monthly Sibyl membership plan. Your current billing cycle is {start} - {end}. You will
        be skipped the billing cycle {pauseAt} -{resumes}. Reactivate to get uninterrupted access to unlimited Tarot
        readings.
      </div>
    );
  } else {
    const planName = currentUser.product?.name ?? "Sibyl";
    content = (
      <>
        <div className={blockClassName}>
          <p>
            You are currently on the <strong>{planName}</strong> Sibyl membership plan. Your current billing cycle is{" "}
            {start} - {end}.
          </p>
          {!!currentUser.nextProduct && (
            <p>
              Your plan is scheduled to change to the <strong>{currentUser.nextProduct.name}</strong> Sibyl plan on your
              next billing cycle.
            </p>
          )}
        </div>
        {!!currentUser.nextProduct && (
          <div>
            <ActionBtn onClick={withModalConfirm(onClickCancel, AccountActionText.cancelNextProductText)}>
              CANCEL PLAN CHANGE
            </ActionBtn>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {modalNode}
      {content}
    </>
  );
});
